import { useState } from 'react'
import { Download } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api, ApiError, saveQuotePdf } from '@/lib/api'
import type { QuoteDetailResponse, QuoteLine } from '@/lib/api-types'
import { formatDate, formatMoney, formatQty } from '@/lib/format'
import { quoteChipClasses, quoteIsExpired } from '@/lib/status'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table'
import { DescriptionList, type DescriptionItem } from '@/components/ui/description-list'
import { PageHeader } from '@/components/ui/page-header'
import { SectionHeader } from '@/components/ui/section-header'
import { Spinner } from '@/components/ui/spinner'
import { StatusChip } from '@/components/ui/status-chip'

export function QuoteDetailPage() {
  const { id = '' } = useParams()

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['quote', id],
    queryFn: ({ signal }) => api.getQuote(id, { signal }),
    staleTime: 15_000,
  })

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner label="Loading this quote…" />
      </div>
    )
  }

  if (error) {
    const missing = error instanceof ApiError && error.status === 404
    return (
      <div className="py-24 text-center">
        <p className="micro-label">{missing ? 'Not found' : 'Something went wrong'}</p>
        <p className="mt-3 text-[0.9375rem] text-inkMid">
          {missing
            ? 'This quote doesn’t exist — or it belongs to another account.'
            : 'We couldn’t load this quote.'}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          {!missing ? (
            <Button variant="neutral" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          ) : null}
          <Button asChild variant="quiet" size="sm">
            <Link to="/quotes">Back to quotes</Link>
          </Button>
        </div>
      </div>
    )
  }

  return <QuoteDetailView detail={data} />
}

// Quantities/uom/totals render VERBATIM from the quote — synced rows carry
// raw SAP UoM values, so no per-kg math happens here.
const LINE_COLUMNS: DataTableColumn<QuoteLine>[] = [
  {
    id: 'product',
    header: 'Product',
    flex: true,
    widthClassName: 'min-w-48',
    cell: (line) => (
      <span className="flex flex-col">
        <span
          className="truncate text-[0.9375rem] font-medium text-ink"
          title={line.productName ?? undefined}
        >
          {line.productName ?? '—'}
        </span>
        <span className="mt-0.5 truncate font-mono text-xs text-mute">
          {[line.packSize, line.supplyModel].filter(Boolean).join(' · ')}
        </span>
      </span>
    ),
  },
  {
    id: 'quantity',
    header: 'Quantity',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (line) => (
      <span>
        {formatQty(line.quantity)}
        {line.uom ? <span className="text-mute"> {line.uom}</span> : null}
      </span>
    ),
  },
  {
    id: 'unitPrice',
    header: 'Unit price',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (line) => (line.pricePerUnit != null ? formatMoney(line.pricePerUnit) : '—'),
  },
  {
    id: 'leadTime',
    header: 'Lead time',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (line) => <span className="text-sm text-inkMid">{line.leadTime ?? '—'}</span>,
  },
  {
    id: 'total',
    header: 'Total',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (line) => (line.lineTotal != null ? formatMoney(line.lineTotal) : '—'),
  },
]

function QuoteDetailView({ detail }: { detail: QuoteDetailResponse }) {
  const { header, lines } = detail
  const expired = quoteIsExpired(header.expirationDate, header.status)
  const [downloading, setDownloading] = useState(false)

  async function downloadPdf() {
    setDownloading(true)
    try {
      await saveQuotePdf(header.id, `green-line-quote-${header.docNum ?? header.id}.pdf`)
    } catch {
      toast.error('The PDF didn’t download. Give it another try.')
    } finally {
      setDownloading(false)
    }
  }

  const facts: DescriptionItem[] = [
    {
      label: 'Issued',
      value: <time dateTime={header.issueDate ?? undefined}>{formatDate(header.issueDate)}</time>,
      mono: true,
    },
  ]
  if (header.expirationDate) {
    facts.push({
      label: expired ? 'Expired' : 'Expires',
      value: (
        <time
          dateTime={header.expirationDate}
          className={cn(expired && 'font-medium text-rust')}
        >
          {formatDate(header.expirationDate)}
        </time>
      ),
      mono: true,
    })
  }
  if (header.paymentTerms) facts.push({ label: 'Terms', value: header.paymentTerms })
  if (header.shippingTerms) facts.push({ label: 'Shipping', value: header.shippingTerms })

  return (
    <article className="mx-auto max-w-data">
      <nav aria-label="Breadcrumb" className="flex items-baseline gap-2 font-mono text-[0.8125rem]">
        <Link
          to="/quotes"
          className="rounded-sm text-mute underline-offset-4 transition-colors duration-[180ms] ease-editorial hover:text-brand hover:underline"
        >
          Quotes
        </Link>
        <span aria-hidden="true" className="text-muteSoft">
          ·
        </span>
        <span aria-current="page" className="text-inkMid">
          {header.docNum ?? '—'}
        </span>
      </nav>

      <PageHeader
        className="mt-3"
        title={`Quote ${header.docNum ?? '—'}`}
        actions={
          <Button onClick={downloadPdf} loading={downloading}>
            {!downloading && <Download aria-hidden="true" />}
            {downloading ? 'Preparing…' : 'Download PDF'}
          </Button>
        }
        meta={
          <StatusChip label={header.status ?? '—'} chipClasses={quoteChipClasses(header.status)} />
        }
      />

      <DescriptionList className="mt-5 border-y border-rule/60 py-4" columns={4} items={facts} />

      {expired ? (
        <p
          role="status"
          className="mt-6 rounded-md border border-rust/30 bg-rust/5 px-4 py-3 text-sm leading-relaxed text-inkMid"
        >
          <span className="font-semibold text-rust">This quote has expired.</span> Ask your Green
          Line rep to refresh the pricing before ordering against it.
        </p>
      ) : null}

      <section aria-labelledby="quote-lines-heading" className="mt-10">
        <SectionHeader id="quote-lines-heading" title="Lines" />
        <div className="mt-4">
          <DataTable<QuoteLine>
            columns={LINE_COLUMNS}
            rows={lines}
            rowKey={(line) => line.id}
            caption="Quote lines"
          />
          <p className="flex justify-end gap-8 border-t border-rule/70 py-3 pr-2">
            <span className="text-sm font-semibold text-ink">Total</span>
            <span className="font-mono text-[0.9375rem] font-semibold tabular-nums text-ink">
              {formatMoney(header.total)}
            </span>
          </p>
        </div>
      </section>

      {header.notes ? (
        <section aria-labelledby="quote-notes-heading" className="mt-10">
          <SectionHeader id="quote-notes-heading" title="Notes" />
          <p className="mt-4 max-w-prose whitespace-pre-line text-sm leading-relaxed text-inkSoft">
            {header.notes}
          </p>
        </section>
      ) : null}
    </article>
  )
}
